/**
 * @module support/customer-login
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
import { SETTINGS } from './../../../../environments/settings';
import {
  AnonymousTokenResponse,
  IamResponse,
} from './../../api/proxies/responses/api-999.interface';
import {
  DnsSettings,
  SetDnsRequest,
  SetDnsResponse,
} from '@support/api/proxies/requests/API_2240.interface';
import { PinVerificationResponse } from '@support/api/proxies/responses/api-2235.interface';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { WEB_API_MAP } from './../../../core/api/proxies/web-api-endpoints';
import { StateStoreUtilsService } from './../../state-object-hash-utilities/utils/state-store-utils.service';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { AppConfig } from './../../../root/app.config';
import { GPC_CONSTS } from '@support/shared/constants/app.constants';
import { PermissionPayloadInterface } from '@root/shared/interfaces/permission-payload.interface';
import Timer = NodeJS.Timer;
import { HttpResponse } from '@angular/common/http';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PrimaryNumersInterface } from '@support/api/proxies/responses/api-2056.interface';
import { UserService } from '@support/shared/services/user.service';
import { CustomerDetailsResponse } from '@support/api/proxies/responses/api-2007.interface';

@Injectable()
export class CustomerProfileService {
  public multipleAccount: boolean;
  /**
   * Broadcasts ermissions api loaded
   */
  public permissionsLoadedSource: Subject<boolean>;
  /**
   * Broadcast about permission api load
   */
  public permissionsLoaded$: Observable<boolean>;
  /**
   * Stores value of modal indicator
   */
  public validateOwnershipModalSubject: Subject<string>;

  /**
   * Timer object to be used ot setInterval / Cancel Interval
   */
  public interval: Timer;

  /**
   * variable to store dns setting flag
   */
  // public isDNSEnabled: boolean;

  constructor(
    private apiGateway: ApiGatewayService,
    private appConfig: AppConfig,
    private stateStoreUtilsService: StateStoreUtilsService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private userService: UserService,
    private objectService: ObjectService
  ) {
    this.permissionsLoadedSource = new Subject();
    this.validateOwnershipModalSubject = new Subject();
    this.permissionsLoaded$ = this.permissionsLoadedSource.asObservable();
  }

  /**
   * Method to get Authorization token
   * @param code
   * @param email
   * @param msisdn
   * @param redirectUri
   * @return IamResponse : Response from the API
   */
  public getAuthToken(
    code: string,
    msisdn: string,
    redirectUri: string,
    userId?: string
  ): Observable<IamResponse> {
    let requestEndPoint = WEB_API_MAP.API_999_getCustomerProfile;
    const CLIENT_ID = this.appConfig.getConfig('authClientId') || SETTINGS.AUTH_CLIENT_ID;
    const dnsSetting = this.featureToggleUtilService.isDNSEnabled();
    let data;
    data =
      `code=${code}&client_id=${CLIENT_ID}&grant_type=${SETTINGS.GRANTTYPE}` +
      `&redirect_uri=${redirectUri}&userId=${userId}&MSISDN=${msisdn}&isDNSEnable=${dnsSetting}`;
    return this.apiGateway
      .postEncodeRequest(requestEndPoint, data, null, null)
      .pipe(map((response: HttpResponse<Object>) => <IamResponse>response.body));
  }

  /**
   * Method to validate ownership by pin verification
   * @return PinVerificationResponse : Response from the API
   */
  public validatePin(pin: string): Observable<PinVerificationResponse> {
    let requestEndPoint = WEB_API_MAP.API_2235_validatePinForOwnership;
    this.stateStoreUtilsService.clearAuthCode();
    const eservicesParams = this.stateStoreUtilsService.getEservicesParams();
    const request = {
      msisdn: eservicesParams.msisdn,
      pin: pin,
      userId: eservicesParams.userId,
      iamToken: this.stateStoreUtilsService.getToken(),
    };
    return this.apiGateway
      .postRequest(requestEndPoint, request, null, null)
      .pipe(map((response: HttpResponse<Object>) => <PinVerificationResponse>response.body));
  }

  /**
   * Method to pop up validate pin modal
   * @return void
   */
  public popVerifyOwnershipModal(modal: string): void {
    this.validateOwnershipModalSubject.next(modal);
  }

  /**
   * Method to get Anonymous Authorization token for web
   * @return AnonymousTokenResponse : Response from the API
   */
  public getAnonymousToken(): Observable<AnonymousTokenResponse> {
    let requestEndPoint = WEB_API_MAP.ANON_TOKEN_API_V6;
    const condition = true; // incase we need to put extra checks for POP Token
    this.stateStoreUtilsService.clearAuthCode();
    const CLIENT_ID = this.appConfig.getConfig('clientId') || SETTINGS.CLIENT_ID;
    const CLIENT_SECRET = this.appConfig.getConfig('clientSecret');
    const GRANT_TYPE = this.appConfig.getConfig('grantType');
    if (condition) {
      let request = {
        client_id: CLIENT_ID,
        cnf: this.objectService.get(self, 'pKey'),
      };
      return this.apiGateway
        .postRequest(requestEndPoint, request, null, null)
        .pipe(map((response: HttpResponse<Object>) => <AnonymousTokenResponse>response.body));
    } else {
      let request = `client_id=${CLIENT_ID}&grant_type=${GRANT_TYPE}`;
      return this.apiGateway
        .postEncodeRequest(requestEndPoint, request, null, null)
        .pipe(map((response: HttpResponse<Object>) => <AnonymousTokenResponse>response.body));
    }
  }

  /**
   * Method to set flag for MultipleAccount
   * @param value
   * @return void
   */
  public setMutipleAccountValue(value: boolean): void {
    this.multipleAccount = value;
  }

  /**
   * Method to invalidate the IAM token
   * @return object
   */
  public invalidateToken(): Observable<Object> {
    const param = {
      token: this.stateStoreUtilsService.getAuthToken(),
    };
    document.cookie = this.appConfig.getConfig('prida_tpp_id_token_unset');
    // removing the interactionId and cartId cookie and session when the user logs out
    setTimeout(() => {
      document.cookie = "interactionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "cartId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      sessionStorage.removeItem('interactionId');
    },0)
    
    return this.apiGateway.getRequest(WEB_API_MAP.API_521_userLogOut, null, null, param);
  }

  /**
   * This is used to get the customer permissions - This is a temp logic
   * @param customerId
   * @param accountId
   * @return Observable<Response>
   */
  public getPermissions(customerId?: string, accountId?: string): Observable<HttpResponse<Object>> {
    const payload: PermissionPayloadInterface = {
      entitlementsDetails: {
        securityProfile: {
          userRole: this.stateStoreUtilsService.getRole() || '',
          customerId: customerId,
          financialAccountNumber: accountId,
        },
      },
    };
    return this.apiGateway.postRequest(API_MAP.API_518_Permissions, payload);
  }

  /**
   * Set Do No Sell in customer profile
   * @param iamData
   * @return Observable<SetDnsResponse>
   */
  public setDNS(iamData: IamResponse): Observable<SetDnsResponse> {
    const dnsSettingsRequest: SetDnsRequest = {
      tmobileId: iamData.tmobileId,
      doNotSellSetting: GPC_CONSTS.DO_NOT_SELL,
      globalPrivacyControlSignal: GPC_CONSTS.YES,
    };
    return this.apiGateway
      .postRequest(WEB_API_MAP.API_2240_setDNS, dnsSettingsRequest)
      .pipe(map((response: HttpResponse<Object>) => <SetDnsResponse>response.body));
  }

  /**
   * Check functional conditions to set Do Not Sell in customer profile
   * @param iamData
   * @param dnsSettings
   * @return boolean
   */
  public callSetDNS(iamData: IamResponse, dnsSettings: DnsSettings): boolean {
    return (
      !(
        dnsSettings.globalPrivacyControlSignal === null ||
        dnsSettings.globalPrivacyControlSignal === GPC_CONSTS.NO
      ) &&
      !(
        iamData.globalDoNotSellSetting === GPC_CONSTS.DO_NOT_SELL &&
        !!iamData.globalDoNotSellSettingDate
      ) &&
      dnsSettings.globalPrivacyControlSignal &&
      (iamData.globalDoNotSellSetting === GPC_CONSTS.SELL ||
        (iamData.globalDoNotSellSetting === GPC_CONSTS.DO_NOT_SELL &&
          !iamData.globalDoNotSellSettingDate))
    );
  }

  /**
   * Method to fetch eligible mobile numbers
   * @returns {Observable<PrimaryNumersInterface>}
   */
  public getEligiblePrimaryMobileNumbers(
    msisdn: string,
    email: string
  ): Observable<PrimaryNumersInterface> {
    const requestParams = {
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      msisdn: msisdn,
      email: email,
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2056_getEligiblePrimaryMobileNumbers, requestParams)
      .pipe(
        map((response: HttpResponse<PrimaryNumersInterface>) => {
          return response.body;
        })
      );
  }

  public getCustomerDetails(): Observable<CustomerDetailsResponse> {
    return this.userService.getCustomerDetailSearch().pipe(
      map((response: CustomerDetailsResponse) => {
        return response;
      })
    );
  }
}
